/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { ShippingCost } from "@eisinc/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitCarrierService";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

type CheckoutState = Readonly<{
    notes: string;
    carrierAccount: string;
    additionalDetails: string;
    lastFour: string;
    expDate: string;
    ccType: string;
    po: string;
    paymentMethod: string;
    paymentProfile: string;
    isCarrierSaving: boolean;
    cardNickName: string;
    cardAddress2: string;
    orderDeliveryBlock?: string;
    shipmentSchedule?: string;
    shippingCostBreakdown?: Record<string, ShippingCost>;
    cardNumberError?: string;
    cvvError?: string;
}>;

const initialCheckoutState: CheckoutState = {
    notes: "",
    carrierAccount: "",
    additionalDetails: "",
    lastFour: "",
    expDate: "",
    ccType: "",
    po: "",
    paymentMethod: "",
    paymentProfile: "",
    isCarrierSaving: false,
    cardNickName: "",
    cardAddress2: "",
    orderDeliveryBlock: "false",
    shipmentSchedule: "",
    shippingCostBreakdown: {} as Record<string, ShippingCost>,
    cardNumberError: "",
    cvvError: "",
};

const reducer = {
    "xCheckout/setNotes": (draft: Draft<CheckoutState>, action: { notes: string }) => {
        draft.notes = action.notes;
    },
    "xCheckout/setCarrierAccount": (draft: Draft<CheckoutState>, action: { carrierAccount: string }) => {
        draft.carrierAccount = action.carrierAccount;
    },
    "xCheckout/setAdditionalDetails": (draft: Draft<CheckoutState>, action: { additionalDetails: string }) => {
        draft.additionalDetails = action.additionalDetails;
    },
    "xCheckout/setLastFour": (draft: Draft<CheckoutState>, action: { lastFour: string }) => {
        draft.lastFour = action.lastFour;
    },
    "xCheckout/setExpDate": (draft: Draft<CheckoutState>, action: { expDate: string }) => {
        draft.expDate = action.expDate;
    },
    "xCheckout/setCcType": (draft: Draft<CheckoutState>, action: { ccType: string }) => {
        draft.ccType = action.ccType;
    },
    "xCheckout/setPo": (draft: Draft<CheckoutState>, action: { po: string }) => {
        draft.po = action.po;
    },
    "xCheckout/setPaymentMethod": (draft: Draft<CheckoutState>, action: { paymentMethod: string }) => {
        draft.paymentMethod = action.paymentMethod;
    },
    "xCheckout/setPaymentProfile": (draft: Draft<CheckoutState>, action: { paymentProfile: string }) => {
        draft.paymentProfile = action.paymentProfile;
    },
    "xCheckout/SetIsCarrierSaving": (draft: Draft<CheckoutState>, action: { isCarrierSaving: boolean }) => {
        draft.isCarrierSaving = action.isCarrierSaving;
    },
    "xCheckout/SetCardNickName": (draft: Draft<CheckoutState>, action: { cardNickName: string }) => {
        draft.cardNickName = action.cardNickName;
    },
    "xCheckout/setCardAddress2": (draft: Draft<CheckoutState>, action: { cardAddress2: string }) => {
        draft.cardAddress2 = action.cardAddress2;
    },
    "xCheckout/setOrderDeliveryBlock": (
        draft: Draft<CheckoutState>,
        action: { orderDeliveryBlock: string | undefined },
    ) => {
        draft.orderDeliveryBlock = action.orderDeliveryBlock;
    },
    "xCheckout/setShipmentSchedule": (draft: Draft<CheckoutState>, action: { shipmentSchedule: string }) => {
        draft.shipmentSchedule = action.shipmentSchedule;
    },
    "xCheckout/setShippingCostBreakdown": (draft: Draft<CheckoutState>, action: { shippingCostBreakdown: Record<string, ShippingCost> }) => {
        draft.shippingCostBreakdown = action.shippingCostBreakdown;
    },
    "xCheckout/setCardNumberError": (draft: Draft<CheckoutState>, action: { error: string }) => {
        draft.cardNumberError = action.error;
    },
    "xCheckout/setCvvError": (draft: Draft<CheckoutState>, action: { error: string }) => {
        draft.cvvError = action.error;
    },
};

const xCheckout = createTypedReducerWithImmer(initialCheckoutState, reducer);

(reducers as any).xCheckout = xCheckout;

export type xCheckoutState = {
    xCheckout: CheckoutState;
};

type xCheckoutReducers = {
    notes: typeof xCheckout;
    carrierAccount: typeof xCheckout;
    additionalDetails: typeof xCheckout;
    lastFour: typeof xCheckout;
    expDate: typeof xCheckout;
    ccType: typeof xCheckout;
    po: typeof xCheckout;
    paymentMethod: typeof xCheckout;
    paymentProfile: typeof xCheckout;
    isCarrierSaving: typeof xCheckout;
    cardNickName: typeof xCheckout;
    cardAddress2: typeof xCheckout;
    orderDeliveryBlock: typeof xCheckout;
};

export type xCheckoutActions = Parameters<xCheckoutReducers[keyof xCheckoutReducers]>[1] | AnyAction;
